import { Stack, type StackProps, Title, type TitleProps } from "@mantine/core";
import type { HTMLAttributes, ReactNode } from "react";

export interface ISSHeadlineProps {
  children: ReactNode;
  header?: ReactNode;
  subTitle?: ReactNode;
  props?: {
    title?: TitleProps & { component?: React.ElementType };
    stack?: StackProps;
    header?: HTMLAttributes<HTMLDivElement>;
  };
}

/**
 * Headline component to display header text
 * can pass header, children and subtitle that will be stacked
 * @param children ReactNode | All texts or sub components, they will be in a h2 if not specified. Somme component build for :
 * - SSWhiteText
 * - SSLoLGradientText
 * @param header ReactNode | Node above the main text, default header can be found at SSHeadLineHeader component
 * @param subTitle ReactNode | Node under the main text
 * @param props | All mantine props for headline sub components, some useful tips :
 * - to remove default headline font family (**font-secret-black**), set the props **ff** on **props.title** as **"unset"**
 * - to change default headline font size (**24**), set the props **fz** on **props.title** as **wanted**
 * - to change default title order (**2**), set the props **order** on **props.title** as **wanted**
 * - the root stack **align and justify on center**, to change it pass the **wanted** state to **align or justify to props.stack**
 * - the root stack has **no gap (0)**, the change it pass the **gap** props as **wanted** to **props.stack**
 */
const SSHeadline: React.FC<ISSHeadlineProps> = ({
  children,
  header,
  subTitle,
  props,
}) => (
  <Stack gap={0} align="center" justify="center" {...props?.stack}>
    {header && <div {...props?.header}>{header}</div>}
    <Title
      order={props?.title?.order ?? 2}
      className="relative z-10 break-words font-secret-black"
      {...props?.title}
    >
      {children}
    </Title>
    {subTitle}
  </Stack>
);

export default SSHeadline;
